/* Utilidades y estilos compartidos de la pestana Sincronizacion.
   Principio UX: el usuario NUNCA ve terminos tecnicos (token, schema, heartbeat).
   Aqui se traducen los estados internos del agente a lenguaje claro. */

export const TIPOS_BD = [
  { v: 'sqlserver', label: 'SQL Server', desc: 'Microsoft SQL Server', puerto: 1433, icon: '🗄️' },
  { v: 'postgresql', label: 'PostgreSQL', desc: 'PostgreSQL', puerto: 5432, icon: '🐘' },
  { v: 'mysql', label: 'MySQL', desc: 'MySQL o MariaDB', puerto: 3306, icon: '🐬' },
  {
    v: 'firebird',
    label: 'Firebird',
    desc: 'Firebird SQL (archivo .fdb/.gdb)',
    puerto: 3050,
    icon: '🔥',
  },
];

export const tipoBdLabel = (v) => (TIPOS_BD.find((t) => t.v === v) || {}).label || v || '—';

/* ── Formateadores de tiempo ── */

export function fmtFecha(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function fmtRelativo(iso) {
  if (!iso) return 'Nunca';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Nunca';
  const seg = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seg < 0) return 'Ahora';
  if (seg < 60) return 'Hace un momento';
  if (seg < 3600) {
    const m = Math.floor(seg / 60);
    return `Hace ${m} min`;
  }
  if (seg < 86400) {
    const h = Math.floor(seg / 3600);
    return `Hace ${h} h`;
  }
  const dias = Math.floor(seg / 86400);
  if (dias === 1) return 'Ayer';
  if (dias < 7) return `Hace ${dias} dias`;
  return fmtFecha(iso);
}

export function fmtDuracion(ms) {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m} min ${r} s`;
}

/* Cuenta regresiva hacia next_sync_at. Devuelve texto legible o null si ya paso. */
export function countdown(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  let seg = Math.floor((d.getTime() - Date.now()) / 1000);
  if (seg <= 0) return 'en breve';
  if (seg >= 3600) {
    const h = Math.floor(seg / 3600);
    const m = Math.floor((seg % 3600) / 60);
    return `${h}h ${String(m).padStart(2, '0')}m`;
  }
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/* ── Estado visual (sin tecnicismos) ── */

export function estadoVisual(sede) {
  const ag = sede && sede.agente;
  if (!sede || !sede.configurada) {
    return { key: 'sin', label: 'Sin configurar', color: '#6b7280', bg: '#f3f4f6', dot: '#9ca3af' };
  }
  if (!ag || ag.estado === 'pendiente_instalacion') {
    return {
      key: 'esperando',
      label: 'Esperando instalacion',
      color: '#d97706',
      bg: '#fffbeb',
      dot: '#f59e0b',
    };
  }
  switch (ag.estado) {
    case 'probando':
      return {
        key: 'probando',
        label: 'Probando conexión',
        color: '#2563eb',
        bg: '#eff6ff',
        dot: '#3b82f6',
        pulse: true,
      };
    case 'activo':
      return ag.online
        ? {
            key: 'online',
            label: 'Conectado',
            color: '#059669',
            bg: '#ecfdf5',
            dot: '#10b981',
            pulse: true,
          }
        : {
            key: 'offline',
            label: 'Sin conexión',
            color: '#6b7280',
            bg: '#f3f4f6',
            dot: '#9ca3af',
          };
    case 'pausado':
      return { key: 'pausado', label: 'Pausada', color: '#d97706', bg: '#fffbeb', dot: '#f59e0b' };
    case 'error':
      return {
        key: 'error',
        label: 'Con problemas',
        color: '#dc2626',
        bg: '#fef2f2',
        dot: '#ef4444',
      };
    default:
      return {
        key: 'esperando',
        label: 'Esperando instalacion',
        color: '#d97706',
        bg: '#fffbeb',
        dot: '#f59e0b',
      };
  }
}

/* Traduce un mensaje de error tecnico a algo accionable, sin stack traces. */
export function mensajeAmigable(err) {
  if (!err) return 'No pudimos conectar con tu base de datos. Revisa los datos e intenta de nuevo.';
  const s = String(err).toLowerCase();
  if (s.includes('timeout') || s.includes('timed out'))
    return 'La conexión tardó demasiado. Verifica que el servidor este encendido y accesible.';
  if (s.includes('login') || s.includes('password') || s.includes('auth'))
    return 'Usuario o contraseña incorrectos. Revisa las credenciales.';
  if (s.includes('refused') || s.includes('could not connect') || s.includes('connection'))
    return 'No se pudo alcanzar el servidor. Revisa la dirección y el puerto.';
  if (s.includes('database') || s.includes('base de datos'))
    return 'No se encontro la base de datos indicada. Revisa el nombre.';
  return 'No pudimos completar la conexión. Revisa los datos e intenta de nuevo.';
}

/* ── Estilos compartidos ── */

export const SS = {
  card: {
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.02)',
    border: '1px solid #eef0f4',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: '5px',
    letterSpacing: '0.1px',
    display: 'block',
  },
  input: {
    width: '100%',
    padding: '11px 14px',
    borderRadius: '10px',
    border: '1.5px solid #e2e4e9',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#111827',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    padding: '11px 22px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: '#111827',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
  btnAccent: {
    padding: '11px 22px',
    borderRadius: '10px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: '#2563eb',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    fontFamily: 'inherit',
    transition: 'opacity 0.15s',
  },
  btnOutline: {
    padding: '10px 18px',
    borderRadius: '10px',
    border: '1.5px solid #e2e4e9',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: '#fff',
    color: '#4a4d59',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    fontFamily: 'inherit',
  },
  btnGhost: {
    padding: '9px 14px',
    borderRadius: '9px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    background: 'transparent',
    color: '#4a4d59',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'inherit',
    width: '100%',
    textAlign: 'left',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,42,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '16px',
    backdropFilter: 'blur(2px)',
  },
  chip: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
  },
};

/* CSS global de la pestana (animaciones + responsive). */
export const SYNC_CSS = `
  @keyframes syncPulse { 0% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5); } 70% { box-shadow: 0 0 0 8px rgba(16,185,129,0); } 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0); } }
  @keyframes syncSpin { to { transform: rotate(360deg); } }
  @keyframes syncIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes syncPop { 0% { transform: scale(0.6); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
  .sync-anim { animation: syncIn 0.25s ease; }
  .sync-spin { display: inline-block; animation: syncSpin 0.8s linear infinite; }
  .sync-dot-pulse { animation: syncPulse 1.8s infinite; }
  .sync-pop { animation: syncPop 0.35s cubic-bezier(0.2,0.8,0.3,1.2); }
  .sync-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
  .sync-menu { position: absolute; right: 0; top: calc(100% + 6px); background: #fff; border: 1px solid #eef0f4; border-radius: 12px; box-shadow: 0 10px 30px rgba(0,0,0,0.12); padding: 6px; z-index: 40; min-width: 210px; }
  .sync-menu button:hover { background: #f5f7fb; }
  .sync-row:hover { background: #f9fafb; }
  @media (max-width: 720px) {
    .sync-modal { width: 100% !important; max-width: 100% !important; height: 100% !important; max-height: 100% !important; border-radius: 0 !important; }
    .sync-modal-body { max-height: none !important; }
    .sync-grid { grid-template-columns: 1fr !important; }
    .sync-actions { flex-direction: column !important; align-items: stretch !important; }
    .sync-actions > * { width: 100% !important; }
    .sync-hide-sm { display: none !important; }
  }
`;
